import * as Sentry from '@sentry/react';

type DebugConsoleLevel = 'error' | 'warn' | 'window_error' | 'unhandled_rejection';

export interface DebugConsoleEntry {
  level: DebugConsoleLevel;
  timestamp: string;
  message: string;
  args: unknown[];
}

export interface DebugSentryEvent {
  eventId: string | null;
  timestamp: string;
  level: string | null;
  message: string | null;
  transaction: string | null;
  traceId: string | null;
  spanId: string | null;
  tags: Record<string, unknown> | null;
  exception: Array<{
    type: string | null;
    value: string | null;
    stacktraceFrames: number | null;
  }>;
}

export interface DebugNetworkEntry {
  timestamp: string;
  method: string;
  url: string;
  status: number | null;
  statusText: string | null;
  ok: boolean | null;
  durationMs: number | null;
  responseBody: unknown;
  error: unknown;
}

export interface DebugDiagnosticsSnapshot {
  console: DebugConsoleEntry[];
  sentry: DebugSentryEvent[];
  network: DebugNetworkEntry[];
}

const MAX_DEBUG_ENTRIES = 50;
const MAX_STRING_LENGTH = 2_000;
const REDACTED = '[redacted]';
const SENSITIVE_KEY_PATTERN = /(authorization|cookie|password|secret|token|api[_-]?key|apikey|jwt|session|refresh|access[_-]?token|service[_-]?role)/i;
const JWT_LIKE_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

const consoleEntries: DebugConsoleEntry[] = [];
const sentryEvents: DebugSentryEvent[] = [];
const networkEntries: DebugNetworkEntry[] = [];
let diagnosticsInstalled = false;

const originalConsoleMethods: Partial<Record<DebugConsoleLevel, (...args: unknown[]) => void>> = {};
let originalFetch: typeof window.fetch | null = null;

function pushBounded<T>(entries: T[], entry: T) {
  entries.push(entry);
  if (entries.length > MAX_DEBUG_ENTRIES) {
    entries.splice(0, entries.length - MAX_DEBUG_ENTRIES);
  }
}

function truncateString(value: string): string {
  if (value.length <= MAX_STRING_LENGTH) {
    return value;
  }

  return `${value.slice(0, MAX_STRING_LENGTH)}…[truncated ${value.length - MAX_STRING_LENGTH} chars]`;
}

export function redactDebugValue(value: unknown, keyHint = '', depth = 0): unknown {
  if (SENSITIVE_KEY_PATTERN.test(keyHint)) {
    return REDACTED;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: truncateString(value.message),
      stack: value.stack ? truncateString(value.stack) : null,
    };
  }

  if (typeof value === 'string') {
    if (JWT_LIKE_PATTERN.test(value)) {
      return REDACTED;
    }
    return truncateString(value);
  }

  if (typeof value === 'number' || typeof value === 'boolean' || value === null || value === undefined) {
    return value;
  }

  if (depth >= 5) {
    return '[max-depth]';
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => redactDebugValue(item, keyHint, depth + 1));
  }

  if (typeof value === 'object') {
    const output: Record<string, unknown> = {};
    for (const [key, nestedValue] of Object.entries(value as Record<string, unknown>).slice(0, 50)) {
      output[key] = redactDebugValue(nestedValue, key, depth + 1);
    }
    return output;
  }

  return String(value);
}

function stringifyConsoleArg(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Error) {
    return `${value.name}: ${value.message}`;
  }

  try {
    return JSON.stringify(redactDebugValue(value));
  } catch {
    return String(value);
  }
}

export function recordDebugConsole(level: DebugConsoleLevel, args: unknown[]) {
  pushBounded(consoleEntries, {
    level,
    timestamp: new Date().toISOString(),
    message: truncateString(args.map(stringifyConsoleArg).join(' ')),
    args: args.map((arg) => redactDebugValue(arg)),
  });
}

function redactDebugUrl(rawUrl: string): string {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(rawUrl, base);
    parsed.searchParams.forEach((value, key) => {
      if (SENSITIVE_KEY_PATTERN.test(key) || JWT_LIKE_PATTERN.test(value)) {
        parsed.searchParams.set(key, REDACTED);
      }
    });
    return truncateString(parsed.toString());
  } catch {
    return truncateString(rawUrl);
  }
}

export function recordDebugNetworkFailure({
  method,
  url,
  status,
  statusText,
  durationMs,
  responseBody = null,
  error = null,
}: {
  method: string;
  url: string;
  status: number | null;
  statusText: string | null;
  durationMs: number | null;
  responseBody?: unknown;
  error?: unknown;
}) {
  pushBounded(networkEntries, {
    timestamp: new Date().toISOString(),
    method: method.toUpperCase(),
    url: redactDebugUrl(url),
    status,
    statusText,
    ok: status === null ? null : false,
    durationMs,
    responseBody: redactDebugValue(responseBody),
    error: redactDebugValue(error),
  });
}

function getFetchUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return input;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.url;
  }

  return String(input);
}

function getFetchMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) {
    return init.method;
  }

  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.method;
  }

  return 'GET';
}

async function readDebugResponseBody(response: Response): Promise<unknown> {
  try {
    const text = await response.clone().text();
    if (!text) {
      return null;
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('json')) {
      try {
        return JSON.parse(text);
      } catch {
        return truncateString(text);
      }
    }

    return truncateString(text);
  } catch (error) {
    return {
      unavailable: true,
      reason: error instanceof Error ? error.message : String(error),
    };
  }
}

function installFetchDiagnostics() {
  if (typeof window.fetch !== 'function' || originalFetch) {
    return;
  }

  originalFetch = window.fetch.bind(window);

  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const startedAt = performance.now();
    const method = getFetchMethod(input, init);
    const url = getFetchUrl(input);

    try {
      const response = await originalFetch?.(input, init);
      if (!response) {
        throw new Error('Fetch returned no response.');
      }

      if (!response.ok) {
        recordDebugNetworkFailure({
          method,
          url: response.url || url,
          status: response.status,
          statusText: response.statusText,
          durationMs: Math.round(performance.now() - startedAt),
          responseBody: await readDebugResponseBody(response),
        });
      }

      return response;
    } catch (error) {
      recordDebugNetworkFailure({
        method,
        url,
        status: null,
        statusText: null,
        durationMs: Math.round(performance.now() - startedAt),
        error,
      });
      throw error;
    }
  }) as typeof window.fetch;
}

function getErrorEventArgs(event: ErrorEvent): unknown[] {
  return [
    event.message,
    {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
    },
  ];
}

function getUnhandledRejectionArgs(event: PromiseRejectionEvent): unknown[] {
  return [event.reason ?? 'Unhandled promise rejection'];
}

function getSentryTraceContext(event: Record<string, any>) {
  const trace = event.contexts?.trace;
  return {
    traceId: typeof trace?.trace_id === 'string' ? trace.trace_id : null,
    spanId: typeof trace?.span_id === 'string' ? trace.span_id : null,
  };
}

export function recordDebugSentryEvent(event: Record<string, any>) {
  const trace = getSentryTraceContext(event);
  const exceptionValues = Array.isArray(event.exception?.values) ? event.exception.values : [];

  pushBounded(sentryEvents, {
    eventId: typeof event.event_id === 'string' ? event.event_id : null,
    timestamp: new Date().toISOString(),
    level: typeof event.level === 'string' ? event.level : null,
    message: typeof event.message === 'string' ? truncateString(event.message) : null,
    transaction: typeof event.transaction === 'string' ? event.transaction : null,
    traceId: trace.traceId,
    spanId: trace.spanId,
    tags: event.tags ? redactDebugValue(event.tags) as Record<string, unknown> : null,
    exception: exceptionValues.slice(0, 3).map((exception: Record<string, any>) => ({
      type: typeof exception.type === 'string' ? exception.type : null,
      value: typeof exception.value === 'string' ? truncateString(exception.value) : null,
      stacktraceFrames: Array.isArray(exception.stacktrace?.frames) ? exception.stacktrace.frames.length : null,
    })),
  });
}

export function installDebugDiagnostics() {
  if (diagnosticsInstalled || typeof window === 'undefined') {
    return;
  }

  diagnosticsInstalled = true;
  originalConsoleMethods.error = console.error.bind(console);
  originalConsoleMethods.warn = console.warn.bind(console);

  console.error = (...args: unknown[]) => {
    recordDebugConsole('error', args);
    originalConsoleMethods.error?.(...args);
  };

  console.warn = (...args: unknown[]) => {
    recordDebugConsole('warn', args);
    originalConsoleMethods.warn?.(...args);
  };

  window.addEventListener('error', (event) => {
    recordDebugConsole('window_error', getErrorEventArgs(event));
  });

  window.addEventListener('unhandledrejection', (event) => {
    recordDebugConsole('unhandled_rejection', getUnhandledRejectionArgs(event));
  });

  installFetchDiagnostics();

  Sentry.addEventProcessor((event) => {
    recordDebugSentryEvent(event as Record<string, any>);
    return event;
  });
}

export function getDebugDiagnosticsSnapshot(): DebugDiagnosticsSnapshot {
  return {
    console: consoleEntries.map((entry) => ({ ...entry })),
    sentry: sentryEvents.map((event) => ({ ...event })),
    network: networkEntries.map((entry) => ({ ...entry })),
  };
}

export function clearDebugDiagnosticsForTests() {
  consoleEntries.splice(0, consoleEntries.length);
  sentryEvents.splice(0, sentryEvents.length);
  networkEntries.splice(0, networkEntries.length);
}
